import * as Common from '@piccolohealth/pbs-common';
import { Spin } from '@piccolohealth/ui';
import React from 'react';
import { Navigate } from 'react-router-dom';
import { useIntercom } from 'react-use-intercom';
import { PiccoloError } from '../components/generic/PiccoloError';
import { useCurrentUserQuery } from '../graphql/hooks/useUserQuery';
import { useAuth } from './AuthContext';

export interface UserContext extends Common.User {
  isLoading: boolean;
}

export const UserContext = React.createContext<UserContext>(null as any);

export const UserContextProvider = (props: React.PropsWithChildren) => {
  const { children } = props;
  const { isAuthenticated, isLoading: authIsLoading } = useAuth();
  const { update: updateIntercom } = useIntercom();
  const {
    isLoading: userIsLoading,
    data,
    error,
  } = useCurrentUserQuery({}, { enabled: isAuthenticated });

  const user = data?.user as Common.User;

  const isLoading = authIsLoading || (userIsLoading && isAuthenticated);

  const content = () => {
    if (error instanceof Common.PiccoloError && error.type === 'AuthUnableToVerifyToken') {
      return <Navigate to='/logout' />;
    }

    if (error) {
      return <PiccoloError error={error} />;
    }

    if (isLoading) {
      return <Spin />;
    }

    return children;
  };

  React.useEffect(() => {
    if (user && !isLoading) {
      updateIntercom({
        userId: user.id,
        name: user.name,
        email: user.email,
        userHash: user.intercomHash,
      });
    }
  }, [user, isLoading, updateIntercom]);

  const value: UserContext = {
    ...user,
    isLoading,
  };

  return <UserContext.Provider value={value}>{content()}</UserContext.Provider>;
};

export const useUser: () => UserContext = () => {
  return React.useContext(UserContext);
};
